/**
 * Utility functions for Driver Group Assignment Settings
 */

/**
 * Convert total seconds to hours, minutes, and seconds
 * @param {number} totalSeconds - Total seconds to convert
 * @returns {Object} Object with hours, minutes, and seconds
 */
export const secondsToTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds };
};

/**
 * Convert hours, minutes, and seconds to total seconds
 * @param {number} hours - Number of hours
 * @param {number} minutes - Number of minutes
 * @param {number} seconds - Number of seconds
 * @returns {number} Total seconds
 */
export const timeToSeconds = (hours, minutes, seconds) => {
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Validate time input value
 * @param {string} value - Input value to validate
 * @param {string} unit - Time unit ('hours', 'minutes', 'seconds')
 * @returns {Object} Validation result { isValid, numValue }
 */
export const validateTimeInput = (value, unit) => {
  // Allow empty string
  if (value === "") {
    return { isValid: true, numValue: null, isEmpty: true };
  }

  // Validate that only numbers are entered
  if (!/^\d+$/.test(value)) {
    return { isValid: false, numValue: null };
  }

  // Parse to integer
  const numValue = parseInt(value, 10);

  // Validate max values for minutes and seconds
  if ((unit === "minutes" || unit === "seconds") && numValue > 59) {
    return { isValid: false, numValue: null };
  }

  // Ensure non-negative
  const validValue = Math.max(0, numValue);

  return { isValid: true, numValue: validValue };
};

/**
 * Convert time inputs to integers, handling both string and number types
 * @param {Object} timeInputs - Object with hours, minutes, seconds
 * @returns {Object} Object with numeric values
 */
export const parseTimeInputs = (timeInputs) => {
  const hours =
    typeof timeInputs.hours === "number"
      ? timeInputs.hours
      : parseInt(timeInputs.hours, 10) || 0;

  const minutes =
    typeof timeInputs.minutes === "number"
      ? timeInputs.minutes
      : parseInt(timeInputs.minutes, 10) || 0;

  const seconds =
    typeof timeInputs.seconds === "number"
      ? timeInputs.seconds
      : parseInt(timeInputs.seconds, 10) || 0;

  return { hours, minutes, seconds };
};

/**
 * Order status options for driver group assignment
 */
export const ORDER_STATUS_OPTIONS = [
  { value: 7, label: "Accepted by business" },
  { value: 4, label: "Ready for pickup" },
];

/**
 * Priority options for driver group assignment
 */
export const PRIORITY_OPTIONS = [
  { value: -1, label: "Low" },
  { value: 0, label: "Normal" },
  { value: 1, label: "High" },
  { value: 2, label: "Very High" },
];
